import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useMutation } from 'urql';
import { useState } from 'react';
import { XLarge } from '@outdoor-voices/ui';
import cc from 'classcat';
import PropTypes from 'prop-types';
import { DT } from '../../../config/constants';
import { QUESTION_ASK_MUTATION } from './queries';
import Button from '../../../components/Button';
import css from './Form.module.scss';
import Field from './Field';
import Overlay from '../../../components/Overlay';
import removeNonASCII from '../../../utils/strings/removeNonASCII';
import Spinner from '../../../components/icons/Spinner';
import useTimeout from '../../../utils/hooks/useTimeout';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var defaultErrorMsg = 'An error occurred trying to submit your question. Please contact support or try again later.';
var successMsg = 'Thank you! A member from Team OV will get back to you shortly.';

var SuccessMessage = function SuccessMessage(_ref) {
  var onSuccess = _ref.onSuccess;
  useTimeout(onSuccess, 5000);
  return /*#__PURE__*/_jsx("span", {
    role: "status",
    children: successMsg
  });
};

SuccessMessage.propTypes = {
  onSuccess: PropTypes.func.isRequired
};

var getInitialInfo = function getInitialInfo() {
  var _window, _window$OV;

  if (!((_window = window) !== null && _window !== void 0 && (_window$OV = _window.OV) !== null && _window$OV !== void 0 && _window$OV.CUSTOMER)) {
    return {
      body: '',
      email: '',
      name: ''
    };
  }

  var _window$OV$CUSTOMER = window.OV.CUSTOMER,
      _window$OV$CUSTOMER$e = _window$OV$CUSTOMER.email,
      email = _window$OV$CUSTOMER$e === void 0 ? '' : _window$OV$CUSTOMER$e,
      _window$OV$CUSTOMER$n = _window$OV$CUSTOMER.name,
      name = _window$OV$CUSTOMER$n === void 0 ? '' : _window$OV$CUSTOMER$n;
  return {
    body: '',
    email: email,
    name: name.split(' ')[0]
  };
};

var QuestionForm = function QuestionForm(_ref2) {
  var onCancelQuestion = _ref2.onCancelQuestion,
      onAskQuestion = _ref2.onAskQuestion,
      on = _ref2.on,
      productId = _ref2.productId,
      title = _ref2.title;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      error = _useState2[0],
      setError = _useState2[1];

  var _useState3 = useState(_objectSpread({}, getInitialInfo())),
      _useState4 = _slicedToArray(_useState3, 2),
      fields = _useState4[0],
      setFields = _useState4[1];

  var _useMutation = useMutation(QUESTION_ASK_MUTATION),
      _useMutation2 = _slicedToArray(_useMutation, 2),
      res = _useMutation2[0],
      executeMutation = _useMutation2[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      submitting = _useState6[0],
      setSubmitting = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      success = _useState8[0],
      setSuccess = _useState8[1];

  var body = fields.body,
      email = fields.email,
      name = fields.name;

  var onChange = function onChange(_ref3) {
    var target = _ref3.target;
    setFields(_objectSpread(_objectSpread({}, fields), {}, _defineProperty({}, target.name, removeNonASCII(target.value))));
  };

  var onSubmit = function onSubmit(event) {
    event.preventDefault();
    var input = {
      body: body,
      email: email,
      name: name,
      product: productId
    };

    if (!name.length) {
      setError('Please enter your name.');
      return false;
    }

    if (body.length <= 5) {
      setError('Please submit a valid question.');
      return false;
    }

    setSubmitting(true);
    return executeMutation({
      input: input
    }).then(function (_ref4) {
      var _questionCreate$error, _questionCreate$error2;

      var resData = _ref4.data,
          resError = _ref4.error;
      setSubmitting(false);

      if (resError) {
        console.error('Question submit error: ', resError);
        setError(defaultErrorMsg);
        return false;
      }

      var questionCreate = resData.questionCreate;

      if (questionCreate !== null && questionCreate !== void 0 && (_questionCreate$error = questionCreate.errors) !== null && _questionCreate$error !== void 0 && (_questionCreate$error2 = _questionCreate$error.messages) !== null && _questionCreate$error2 !== void 0 && _questionCreate$error2.length) {
        console.error('Question submit error: ', questionCreate.errors);
        var errors = questionCreate.errors.messages.join('. ');
        setError(errors);
        return false;
      }

      setError(false);
      setSuccess(true);
      return true;
    });
  };

  var onSuccess = function onSuccess() {
    setSuccess(false);
    setFields({
      name: '',
      email: '',
      body: ''
    });
    onCancelQuestion();
  };

  var getButtonLabel = function getButtonLabel() {
    if (submitting) {
      return DT;
    }

    if (success) {
      return 'Sent!';
    }

    return 'Submit my question';
  };

  return /*#__PURE__*/_jsx(Overlay, {
    label: "Ask a product question",
    on: on,
    onHide: onCancelQuestion,
    onShow: onAskQuestion,
    children: /*#__PURE__*/_jsxs("form", {
      className: cc([css.root, _defineProperty({}, css.on, on)]),
      onSubmit: onSubmit,
      children: [/*#__PURE__*/_jsxs("div", {
        className: cc([css.wrap, _defineProperty({}, css.shake, !!error)]),
        children: [res.fetching && !error && !success && /*#__PURE__*/_jsx("div", {
          className: css.spinner,
          children: /*#__PURE__*/_jsx(Spinner, {})
        }), /*#__PURE__*/_jsxs("header", {
          className: css.header,
          children: [/*#__PURE__*/_jsx("span", {
            className: css.description,
            children: "Ask a question about:"
          }), /*#__PURE__*/_jsx("h2", {
            className: css.title,
            children: title
          }), /*#__PURE__*/_jsx("div", {
            className: cc([css.success, _defineProperty({}, css.show, success)]),
            children: success && /*#__PURE__*/_jsx(SuccessMessage, {
              onSuccess: onSuccess
            })
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: cc([css.fields, _defineProperty({}, css.hide, success)]),
          children: [/*#__PURE__*/_jsx(Field, {
            ariaLabel: "Enter your first name",
            autoComplete: "given-name",
            id: "name",
            label: "First Name",
            onChange: onChange,
            required: true,
            type: "text",
            value: fields.name
          }), /*#__PURE__*/_jsx(Field, {
            ariaLabel: "Enter your email",
            autoComplete: "email",
            helpText: "Your email won\u2019t be shown on the submission and we never share emails with other companies.",
            id: "email",
            label: "Email",
            onChange: onChange,
            required: true,
            type: "email",
            value: fields.email
          }), /*#__PURE__*/_jsx(Field, {
            ariaLabel: "Ask your question here.",
            id: "body",
            label: "What would you like to know?",
            name: "body",
            onChange: onChange,
            required: true,
            type: "textarea",
            value: fields.body
          }), error && /*#__PURE__*/_jsx("p", {
            className: css.error,
            role: "status",
            children: error
          })]
        }), /*#__PURE__*/_jsx("footer", {
          className: css.footer,
          children: /*#__PURE__*/_jsx(Button, {
            "aria-label": "Click to submit your product question",
            className: cc([css.submit, _defineProperty({}, css.disabled, success)]),
            disabled: submitting || success,
            onClick: onSubmit,
            type: "submit",
            children: getButtonLabel()
          })
        })]
      }), /*#__PURE__*/_jsx("button", {
        "aria-label": "Click to close product question dialog",
        className: css.closeButton,
        onClick: onCancelQuestion,
        type: "button",
        children: /*#__PURE__*/_jsx(XLarge, {})
      })]
    })
  });
};

QuestionForm.propTypes = {
  on: PropTypes.bool.isRequired,
  onCancelQuestion: PropTypes.func.isRequired,
  onAskQuestion: PropTypes.func.isRequired,
  productId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
};
export default QuestionForm;